import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Checkbox, Alert, Paper, Box, Button, Grid
} from '@mui/material';

import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useErrorManager from '../../hooks/useErrorManager';
import { useNotification } from '../../contexts/NotificationContext';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import PageContainer from '../Layout/PageContainer';

const modes = [
  { key: 'Installation', label: 'Installation mode' },
  { key: 'ExtendedInstallation', label: 'Extended installation mode' },
  { key: 'ProtectedInstallation', label: 'Protected installation mode' },
  { key: 'ExtendedStandard', label: 'Extended standard mode' },
  { key: 'ExtendedSearch', label: 'Extended search mode' },
  { key: 'Deletion', label: 'Deletion mode' },
];

function ChangeNetworkMode() {
  const { networkId } = useParams();
  
  const navigate = useNavigate();
  const axiosInstance = useAxiosPrivate();

  const { setError, clearError } = useErrorManager();
  const { showNotification } = useNotification();
  const { updateBreadcrumb } = useBreadcrumb();

  const [featureStates, setFeatureStates] = useState({});

  useEffect(() => {
        updateBreadcrumb([
            { label: 'Manage networks', link: '/networks' },
            { label: 'Network', link: `/networks/${networkId}`  }, 
            { label: 'Change mode', link: null } // Dynamically set location name
        ]);
  }, [networkId]);


  const handleChange = (modeKey, type) => {
    setFeatureStates((prev) => {
      const current = prev[modeKey];
      let updatedValue = null;

      if (current === type) {
        updatedValue = null; // toggle off if already selected
      } else {
        updatedValue = type;
      }

      return {
        ...prev,
        [modeKey]: updatedValue
      };
    });
  };

  const buildPayload = () =>
    Object.entries(featureStates)
      .filter(([, value]) => value)
      .map(([key, value]) => `${key}${value}`);

  const handleSubmit = async () => {
    const actions = buildPayload();
    try {
      const payload = {
        networkIdList: [networkId],
        rightnow: true,
        time: new Date(),
        actions,
      };

      await axiosInstance.put(`/net/${networkId}/changemode`, payload);
      clearError('edit');
      showNotification('Network mode updated successfully', 'success');
      navigate(`/networks/${networkId}`);
    } catch (err) {
      setError('edit', 'An error occurred while saving changes.');
    }
  };

  const handleCancel = () => {
        navigate(`/networks/${networkId}`);
  };

  return (
    <PageContainer title="Change Network Mode">
      
      <Grid container spacing={1} sx={{ marginBottom: '20px' }}>
          <Grid item xs={8}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>NETWORK MODE</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">ON</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="center">OFF</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {modes.map(({ key, label }) => (
                        <TableRow key={key}>
                          <TableCell>{label}</TableCell>
                          <TableCell align="center">
                            <Checkbox
                              checked={featureStates[key] === 'Enable'}
                              onChange={() => handleChange(key, 'Enable')}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Checkbox
                              checked={featureStates[key] === 'Disable'}
                              onChange={() => handleChange(key, 'Disable')}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
          </Grid>
          <Grid item xs={4}>
            <Box display="flex" flexDirection="column" gap={2}>
              {featureStates['Installation'] === 'Enable' && (
                <Alert
                  severity="info"
                  sx={{ display: 'flex', alignItems: 'center', borderRadius: 2 }}
                >
                  Enabling the installation mode may lead to unintentionally merging of two networks in case this mode is also enabled in other networks of the neighborhood.
                </Alert>
              )}

              {featureStates['ExtendedInstallation'] === 'Enable' && (
                <Alert
                  severity="info"
                  sx={{ display: 'flex', alignItems: 'center', borderRadius: 2 }}
                >
                  Enabling the extended installation mode may lead to integrating unwanted meters from the neighborhood in your network until the max. number of meters in the network is reached.
                </Alert>
              )}

              {(featureStates['ExtendedInstallation'] === 'Enable' ||
                featureStates['ExtendedStandard'] === 'Enable' ||
                featureStates['ExtendedSearch'] === 'Enable') && (
                <Alert
                  severity="info"
                  sx={{ display: 'flex', alignItems: 'center', borderRadius: 2 }}
                >
                  Enabling an extended network mode requires a lot of energy and will reduce the lifetime of the network.
                </Alert>
              )}
            </Box>
          </Grid>

      </Grid>       
      

      <Box display="flex" justifyContent="flex-start" gap={2}>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleSubmit}
          disabled={
            Object.values(featureStates).every((val) => val === null || val === undefined)
          }
        >
          SAVE
        </Button>
        <Button
            variant="outlined"
            color="secondary"
            onClick={handleCancel}
        >
            Cancel
        </Button>
      </Box>
    </PageContainer>
  );
}

export default ChangeNetworkMode;
